import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
    Box,
    Button,
    Card,
    CardContent,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    CircularProgress,
    IconButton,
    Divider,
    MenuItem,
    Tooltip,
    Alert
} from '@mui/material';
import {
    ArrowBack,
    Print,
    Download
} from '@mui/icons-material';
import styled from '@emotion/styled';
import { getInvoice, recordClientPayment } from '../redux/ducks/invoices';
import { downloadInvoicePdf } from '../utils/downloadUtils';
import AppDatePicker from './AppDatePicker';
import { formatDate, formatDayLong, formatMonthKey, toLocalDay } from '../utils/dateUtils';

const LoadingWrapper = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(3),
}));

const PageBox = styled(Box)(({ theme }) => ({
    padding: theme.spacing(3),
}));

const StackedAlert = styled(Alert)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));

const BackButton = styled(Button)(({ theme }) => ({
    marginTop: theme.spacing(2),
}));

const HeaderRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(3),
    gap: theme.spacing(2),
}));

const CreditMemoChip = styled(Chip)(({ theme }) => ({
    marginLeft: theme.spacing(2),
}));

const ActionButtonsRow = styled(Box)(({ theme }) => ({
    marginLeft: 'auto',
    display: 'flex',
    gap: theme.spacing(1),
}));

const InvoiceCard = styled(Card)(({ theme }) => ({
    marginBottom: theme.spacing(3),
}));

const InvoiceMetaRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(3),
}));

const MetaColumn = styled(Box)({
    textAlign: 'right',
});

const StatusBadge = styled(Chip)(({ theme }) => ({
    marginBottom: theme.spacing(1),
}));

const SectionDivider = styled(Divider)(({ theme }) => ({
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
}));

const SectionBox = styled(Box)(({ theme }) => ({
    marginTop: theme.spacing(3),
}));

const DateCell = styled(TableCell)({
    whiteSpace: 'nowrap',
    width: 1,
});

const PaymentsTableContainer = styled(TableContainer)(({ theme }) => ({
    marginTop: theme.spacing(1),
}));

const CoveredAmount = styled('span')(({ theme }) => ({
    borderBottom: `1px dotted ${theme.palette.text.disabled}`,
    color: theme.palette.text.secondary,
    cursor: 'help',
}));

const ClientInvoiceView = () => {
    const { clientSlug, invoiceId } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [invoice, setInvoice] = useState(null);
    const [loading, setLoading] = useState(true);
    const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
    const [paymentData, setPaymentData] = useState({
        amount: '',
        date: toLocalDay(),
        method: '',
        externalId: '',
        notes: ''
    });
    const [submittingPayment, setSubmittingPayment] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        loadInvoice();
    }, [invoiceId]);

    const loadInvoice = () => {
        setLoading(true);
        setError(null);
        dispatch(getInvoice(invoiceId, (response) => {
            setLoading(false);
            if (response) {
                setInvoice(response);
            } else {
                setError('Invoice not found or you do not have access to it');
            }
        }));
    };

    const handleDownload = async () => {
        setDownloading(true);
        try {
            await downloadInvoicePdf(dispatch, invoice._id, invoice.invoiceNumber);
        } catch (err) {
            setError(err.message || 'Failed to download PDF');
        } finally {
            setDownloading(false);
        }
    };

    const handleRecordPayment = () => {
        if (!paymentData.amount || !paymentData.date || !paymentData.method) {
            setError('Please fill in all required fields: Amount, Date, and Payment Method');
            return;
        }

        setSubmittingPayment(true);
        setError(null);
        dispatch(recordClientPayment(invoiceId, paymentData, (response) => {
            setSubmittingPayment(false);
            if (response) {
                setPaymentDialogOpen(false);
                setPaymentData({
                    amount: '',
                    date: toLocalDay(),
                    method: '',
                    externalId: '',
                    notes: ''
                });
                loadInvoice();
            }
        }));
    };

    const formatBillingPeriod = (startDate, endDate) => {
        const day = formatDate(endDate || startDate);
        return day ? formatMonthKey(`${day.slice(0, 4)}${day.slice(5, 7)}`) : '';
    };

    const formatQuantity = (item) => {
        if (item.isCharge || item.isRetainerBase) return '1';
        const quantity = Number(item.quantity);
        return Number.isFinite(quantity) ? `${quantity.toFixed(2)} hrs` : '-';
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'Paid': return 'success';
            case 'Draft': return 'warning';
            case 'Open': return 'info';
            case 'Overdue': return 'error';
            default: return 'default';
        }
    };

    if (loading) {
        return (
            <LoadingWrapper>
                <CircularProgress />
            </LoadingWrapper>
        );
    }

    if (error && !invoice) {
        return (
            <PageBox>
                <StackedAlert severity="error">{error}</StackedAlert>
                <Button startIcon={<ArrowBack />} onClick={() => navigate(`/client/${clientSlug}`)}>
                    Back to Client
                </Button>
            </PageBox>
        );
    }

    if (!invoice) {
        return (
            <PageBox>
                <Typography variant="h6">Invoice not found</Typography>
                <BackButton startIcon={<ArrowBack />} onClick={() => navigate(`/client/${clientSlug}`)}>
                    Back to Client
                </BackButton>
            </PageBox>
        );
    }

    let lineItems = [];

    if (invoice.lineItems && invoice.lineItems.length > 0) {
        lineItems = invoice.lineItems.map(item => ({
            description: item.description,
            date: item.date,
            quantity: item.hours,
            rate: item.rate || 0,
            amount: item.amount || 0,
            billingType: item.billingType,
            isRetainerBase: item.isRetainerBase,
            isRetainerOverage: item.isRetainerOverage,
            isCharge: item.billingType === 'Charge' || item.billingType === 'Credit' || item.isPayment,
            isCoveredByRetainer: item.billingType === 'Retainer' && !item.isRetainerBase && !item.isRetainerOverage && (item.amount || 0) === 0
        }));
    } else {
        if (invoice.timeRecords && invoice.timeRecords.length > 0) {
            const grouped = {};
            invoice.timeRecords.forEach(tr => {
                const key = `${tr.parentType}-${tr.parentId?._id || tr.parentId}`;
                if (!grouped[key]) {
                    grouped[key] = {
                        description: tr.parentId?.name || 'Unknown',
                        quantity: 0,
                        rate: 0,
                        amount: 0,
                        timeRecords: []
                    };
                }
                const hours = (tr.time || 0) / 60;
                grouped[key].quantity += hours;
                grouped[key].timeRecords.push(tr);
            });
            lineItems.push(...Object.values(grouped));
        }

        if (invoice.staticCharges && invoice.staticCharges.length > 0) {
            invoice.staticCharges.forEach(charge => {
                lineItems.push({
                    description: charge.name || charge.description,
                    date: charge.date,
                    quantity: 1,
                    rate: charge.amount || 0,
                    amount: charge.amount || 0,
                    isCharge: true,
                });
            });
        }
    }

    const totalPayments = invoice.payments ? invoice.payments.reduce((sum, payment) => sum + (payment.amount || 0), 0) : 0;
    const remainingBalance = invoice.amount - totalPayments;

    return (
        <>
            <style>{`
                @media print {
                    @page {
                        margin: 0.5in;
                    }
                    body * {
                        visibility: hidden;
                    }
                    .invoice-print-content,
                    .invoice-print-content * {
                        visibility: visible;
                    }
                    .invoice-print-content {
                        position: absolute;
                        left: 0;
                        top: 0;
                        width: 100%;
                    }
                    .no-print {
                        display: none !important;
                    }
                }
            `}</style>
            <PageBox>
                <HeaderRow className="no-print">
                <IconButton onClick={() => navigate(`/client/${clientSlug}`)}>
                    <ArrowBack />
                </IconButton>
                <Typography variant="h4">
                    Invoice #{invoice.invoiceNumber}
                    {invoice.isCredit && <CreditMemoChip label="Credit Memo" size="small" color="secondary" />}
                </Typography>
                <ActionButtonsRow>
                    <Button startIcon={<Print />} onClick={() => window.print()}>
                        Print
                    </Button>
                    <Button startIcon={downloading ? <CircularProgress size={18} /> : <Download />} onClick={handleDownload} disabled={downloading}>
                        {downloading ? 'Downloading...' : 'Download'}
                    </Button>
                    {invoice.status !== 'Paid' && remainingBalance > 0 && false && (
                        <Button
                            variant="contained"
                            onClick={() => setPaymentDialogOpen(true)}
                        >
                            Record Payment
                        </Button>
                    )}
                </ActionButtonsRow>
            </HeaderRow>

            {error && (
                <StackedAlert severity="error" className="no-print" onClose={() => setError(null)}>
                    {error}
                </StackedAlert>
            )}

            <InvoiceCard className="invoice-print-content">
                <CardContent>
                    <InvoiceMetaRow>
                        <Box>
                            <Typography variant="h6" gutterBottom>
                                {invoice.client?.name || 'Unknown Client'}
                                {(invoice.billingPeriodStart || invoice.billingPeriodEnd) && ` - ${formatBillingPeriod(invoice.billingPeriodStart, invoice.billingPeriodEnd)}`}
                            </Typography>
                            {invoice.client?.address && (
                                <Typography variant="body2" color="text.secondary">
                                    {invoice.client.address.street && `${invoice.client.address.street}, `}
                                    {invoice.client.address.city && `${invoice.client.address.city}, `}
                                    {invoice.client.address.state && `${invoice.client.address.state} `}
                                    {invoice.client.address.postalCode}
                                </Typography>
                            )}
                            {invoice.client?.email && (
                                <Typography variant="body2" color="text.secondary">
                                    {invoice.client.email}
                                </Typography>
                            )}
                        </Box>
                        <MetaColumn>
                            <StatusBadge label={invoice.status} color={getStatusColor(invoice.status)} />
                            <Typography variant="body2" color="text.secondary">
                                Invoice Date: {formatDayLong(invoice.invoiceDate)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Due Date: {formatDayLong(invoice.dueDate)}
                            </Typography>
                            {invoice.sentDate && (
                                <Typography variant="body2" color="text.secondary">
                                    Sent: {formatDayLong(invoice.sentDate)}
                                </Typography>
                            )}
                            {invoice.paidDate && (
                                <Typography variant="body2" color="text.secondary">
                                    Paid: {formatDayLong(invoice.paidDate)}
                                </Typography>
                            )}
                        </MetaColumn>
                    </InvoiceMetaRow>

                    <SectionDivider />

                    <TableContainer component={Paper} variant="outlined">
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <DateCell>
                                        <Tooltip title="Date of the work on this line. Retainer and overage lines cover a whole month, so they show its last day.">
                                            <span>Date</span>
                                        </Tooltip>
                                    </DateCell>
                                    <TableCell>Description</TableCell>
                                    <TableCell>Type</TableCell>
                                    <TableCell align="right">Quantity/Hours</TableCell>
                                    <TableCell align="right">Rate</TableCell>
                                    <TableCell align="right">Amount</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {lineItems.map((item, index) => (
                                    <TableRow key={index} hover>
                                        <DateCell>{formatDayLong(item.date)}</DateCell>
                                        <TableCell>{item.description}</TableCell>
                                        <TableCell>{item.billingType || 'Hourly'}</TableCell>
                                        <TableCell align="right">
                                            {formatQuantity(item)}
                                        </TableCell>
                                        <TableCell align="right">
                                            ${(item.rate || 0).toFixed(2)}
                                        </TableCell>
                                        <TableCell align="right">
                                            {item.isCoveredByRetainer ? (
                                                <Tooltip title="These hours are covered by your retainer, so there is no extra charge for them. The retainer charge itself may appear on a different invoice.">
                                                    <CoveredAmount>$0.00</CoveredAmount>
                                                </Tooltip>
                                            ) : (
                                                `$${(item.amount || 0).toFixed(2)}`
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                                <TableRow>
                                    <TableCell colSpan={5} align="right">
                                        <Typography variant="body1" fontWeight="bold">
                                            Subtotal:
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography variant="body1" fontWeight="bold">
                                            ${(invoice.subtotal || 0).toFixed(2)}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                                {invoice.taxAmount > 0 && (
                                    <TableRow>
                                        <TableCell colSpan={5} align="right">
                                            <Typography variant="body1">
                                                Tax ({invoice.taxRate}%):
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="body1">
                                                ${(invoice.taxAmount || 0).toFixed(2)}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                                <TableRow>
                                    <TableCell colSpan={5} align="right">
                                        <Typography variant="h6" fontWeight="bold">
                                            Total:
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography variant="h6" fontWeight="bold">
                                            ${(invoice.amount || 0).toFixed(2)} {invoice.currency || 'USD'}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {invoice.payments && invoice.payments.length > 0 && (
                        <SectionBox>
                            <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                                Payment History
                            </Typography>
                            <PaymentsTableContainer component={Paper} variant="outlined">
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Date</TableCell>
                                            <TableCell>Method</TableCell>
                                            <TableCell>Amount</TableCell>
                                            {invoice.payments.some(p => p.externalId) && (
                                                <TableCell>External ID</TableCell>
                                            )}
                                            {invoice.payments.some(p => p.notes) && (
                                                <TableCell>Notes</TableCell>
                                            )}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {invoice.payments.map((payment, index) => (
                                            <TableRow key={index}>
                                                <TableCell>{formatDayLong(payment.date)}</TableCell>
                                                <TableCell>{payment.method}</TableCell>
                                                <TableCell>${(payment.amount || 0).toFixed(2)}</TableCell>
                                                {invoice.payments.some(p => p.externalId) && (
                                                    <TableCell>{payment.externalId || '-'}</TableCell>
                                                )}
                                                {invoice.payments.some(p => p.notes) && (
                                                    <TableCell>{payment.notes || '-'}</TableCell>
                                                )}
                                            </TableRow>
                                        ))}
                                        <TableRow>
                                            <TableCell colSpan={invoice.payments.some(p => p.externalId) ? 3 : 2} align="right">
                                                <Typography variant="body1" fontWeight="bold">
                                                    Total Paid:
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body1" fontWeight="bold">
                                                    ${totalPayments.toFixed(2)}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                        {remainingBalance > 0 && (
                                            <TableRow>
                                                <TableCell colSpan={invoice.payments.some(p => p.externalId) ? 3 : 2} align="right">
                                                    <Typography variant="body1" fontWeight="bold">
                                                        Remaining Balance:
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body1" fontWeight="bold" color="error">
                                                        ${remainingBalance.toFixed(2)}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </PaymentsTableContainer>
                        </SectionBox>
                    )}

                    {invoice.notes && (
                        <SectionBox>
                            <Typography variant="subtitle2" gutterBottom>
                                Notes:
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {invoice.notes}
                            </Typography>
                        </SectionBox>
                    )}
                </CardContent>
            </InvoiceCard>

            {/* Payment Recording Dialog */}
            <Dialog open={paymentDialogOpen} onClose={() => setPaymentDialogOpen(false)} maxWidth="sm" fullWidth className="no-print">
                <DialogTitle>Record Payment</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        label="Payment Amount *"
                        type="number"
                        value={paymentData.amount}
                        onChange={(e) => setPaymentData({ ...paymentData, amount: e.target.value })}
                        margin="normal"
                        inputProps={{ step: "0.01", min: "0" }}
                    />
                    <Box mt={2} mb={1}>
                        <AppDatePicker
                            label="Payment Date"
                            value={paymentData.date}
                            onChange={(v) => setPaymentData({ ...paymentData, date: v })}
                            required
                        />
                    </Box>
                    <TextField
                        fullWidth
                        select
                        label="Payment Method *"
                        value={paymentData.method}
                        onChange={(e) => setPaymentData({ ...paymentData, method: e.target.value })}
                        margin="normal"
                    >
                        <MenuItem value="Check">Check</MenuItem>
                        <MenuItem value="Wire Transfer">Wire Transfer</MenuItem>
                        <MenuItem value="Credit Card">Credit Card</MenuItem>
                        <MenuItem value="PayPal">PayPal</MenuItem>
                        <MenuItem value="Other">Other</MenuItem>
                    </TextField>
                    <TextField
                        fullWidth
                        label="External ID (optional)"
                        value={paymentData.externalId}
                        onChange={(e) => setPaymentData({ ...paymentData, externalId: e.target.value })}
                        margin="normal"
                        helperText="For tracking payment in external systems"
                    />
                    <TextField
                        fullWidth
                        label="Notes (optional)"
                        multiline
                        rows={3}
                        value={paymentData.notes}
                        onChange={(e) => setPaymentData({ ...paymentData, notes: e.target.value })}
                        margin="normal"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPaymentDialogOpen(false)} disabled={submittingPayment}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleRecordPayment}
                        variant="contained"
                        disabled={submittingPayment}
                    >
                        {submittingPayment ? <CircularProgress size={20} /> : 'Record Payment'}
                    </Button>
                </DialogActions>
            </Dialog>
            </PageBox>
        </>
    );
};

export default ClientInvoiceView;
